/**
 * Command-queue bridge. Drains the zustand command queue for any command
 * whose entityType (and targetPath, for route-filter) matches the current
 * CustomDash page. Commands the drawer emits just before `navigate()` wait
 * in the queue until the target page mounts this bridge, then fire.
 *
 * Key property: commands survive navigation. The old synchronous-handler
 * design (and its `bindingRef` predecessor) lost commands emitted before
 * the target page's bridge had registered.
 */
import mixpanel from 'mixpanel-browser';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';

import { BuildADashTrackingEvents } from '@revelio/core';

import { useSkillTaxonomy } from '../../shared/user-preferences/hooks/useSkillTaxonomySelection';
import { convertTabContentToLLM } from '../CustomDash/components/AIAssistant/convert-tab-content-to-llm';
import {
  applyLlmTabContentToTabDispatch,
  PRIMARY_FILTER_BY_AI_ENTITY,
} from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import { getEntityTypeFromPrimaryFilter } from '../CustomDash/components/EditableGrid/getEntityTypeFromPrimaryFilter';
import { useEditMode } from '../CustomDash/components/EditModeProvider/EditModeProvider';
import {
  usePageConfig,
  useTabContentConfig,
} from '../CustomDash/components/PageConfigProvider/PageConfigProvider';
import {
  PageType,
  RevelioPageId,
} from '../CustomDash/components/PageConfigProvider/types';
import { buildAiSearchCurrentFilterContext } from '../CustomDash/filters/ai-search/buildAiSearchCurrentFilterContext';
import {
  getRoleTaxonomyIdForAiSearch,
  getSkillTaxonomyIdFromActiveFilterMap,
} from '../CustomDash/filters/ai-search/getAiSearchTaxonomyFromFilterMap';
import { useApplyAiSearchFilters } from '../CustomDash/filters/ai-search/useApplyAiSearchFilters';
import {
  useFilterContext,
  useIsTaxonomyResolved,
} from '../CustomDash/filters/contexts/FilterProvider';
import { isDefaultUntitledName } from '../CustomDash/utils/generate-unique-title';
import {
  filterIdToEntityType,
  getPrimaryFilterIdForScreenerV2Page,
  isScreenerV2Page,
} from '../CustomDash/utils/routing-helpers';
import { formatClarificationQuestionsAsText } from './format-clarification-questions';
import {
  NarrationCollector,
  setActiveNarrationCollector,
} from './ga-narration-collection-context';
import {
  type CustomDashAssistantEntityType,
  type GlobalAssistantEntityType,
  toCustomDashAssistantEntityType,
} from './global-ai-assistant-api';
import {
  type GlobalAiCommand,
  useGlobalAiCommandStore,
} from './global-ai-assistant-command-store';
import { useGlobalAIAssistantOptional } from './global-ai-assistant-context';
import {
  type CachedPageStreamResult,
  readCachedPageStream,
  writeCachedPageStream,
} from './global-ai-assistant-page-stream-cache';
import {
  isScreenerAiOutput,
  isTalentDiscoveryFilterResponse,
  isTalentDiscoveryPublicPath,
} from './global-ai-assistant-route-filter-kind';
import { dashboardStreamEventToProgress } from './global-ai-page-gen-progress';
import {
  runDashboardPageStreamCreate,
  runDashboardPageStreamUpdate,
} from './global-ai-run-page-stream';
import { globalAssistantPathsMatch } from './global-assistant-route-filter-path';
import {
  buildFilterResolutionDigest,
  summarizeAppliedChartFilters,
  withChartScope,
} from './turn-outcome-digest';
import { useGlobalAIAssistantFeatureEnabled } from './use-global-ai-assistant-feature-enabled';
import { useGlobalAssistantTaxonomy } from './use-global-ai-assistant-taxonomy';

function getPrimaryFilterForAssistantEntity(
  entityType: CustomDashAssistantEntityType
) {
  return PRIMARY_FILTER_BY_AI_ENTITY[entityType];
}

type GlobalAiBridgeCommandMatchContext = {
  editMode: boolean;
  entityTypeNow: GlobalAssistantEntityType | null;
  pathname: string;
};

function getPreviousPromptsForUpdateCommand(
  command: Extract<
    GlobalAiCommand,
    { kind: 'startCustomPageStream'; mode: 'update' }
  >
): string[] {
  const currentPrompt = command.prompt.trim();
  return (
    command.conversation?.turns
      .filter((turn) => turn.role === 'user')
      .map((turn) => turn.content.trim())
      .filter(Boolean)
      .filter((prompt) => prompt !== currentPrompt)
      .slice(-3) ?? []
  );
}

export function shouldGlobalAiBridgeHandleCommand(
  command: GlobalAiCommand,
  { editMode, entityTypeNow, pathname }: GlobalAiBridgeCommandMatchContext
) {
  if (entityTypeNow === null || command.entityType !== entityTypeNow) {
    return false;
  }

  if (command.kind === 'applyRouteFilters') {
    return globalAssistantPathsMatch(pathname, command.targetPath);
  }

  if (command.kind === 'applySingleChart') {
    return editMode && globalAssistantPathsMatch(pathname, command.targetPath);
  }

  if (command.mode === 'update') {
    // Update mode intentionally targets the currently mounted edit page.
    // Unlike draft-opening commands, it has no future destination path.
    return editMode;
  }

  return editMode && globalAssistantPathsMatch(pathname, command.targetPath);
}

export function GlobalAIAssistantBridge() {
  const enabled = useGlobalAIAssistantFeatureEnabled();
  const ctx = useGlobalAIAssistantOptional();
  const { pathname } = useLocation();
  const {
    config: pageConfig,
    dispatch: pageDispatch,
    discardChanges,
    hasUnsavedChanges,
  } = usePageConfig();
  const { editMode } = useEditMode();
  const { config: tabContent, dispatch } = useTabContentConfig();
  const { activeFilterMap } = useFilterContext();
  // TODO(backend-adaptive): the two readiness hooks below + the
  // `isRoleTaxonomyResolved && isSkillTaxonomyResolved` gate in the
  // publish effect below exist to avoid publishing a stale pre-resolution
  // taxonomy into the store (which the drawer would then use for a wasted
  // fetch under the wrong cache key). Once the backend accepts "adaptive"
  // and the drawer no longer needs to mirror the adaptive-resolved value,
  // this whole readiness gate can go. See the refinement-pass docblock in
  // `global-ai-assistant-drawer.tsx` for context.
  const isRoleTaxonomyResolved = useIsTaxonomyResolved();
  // `useSkillTaxonomy` returns `undefined` while adaptive skill taxonomy
  // is still resolving; `number` (including 0 for a non-adaptive user
  // with default preference) once settled.
  const resolvedSkillTaxonomy = useSkillTaxonomy();
  const isSkillTaxonomyResolved = resolvedSkillTaxonomy !== undefined;
  // Used for the page-stream cache key: composes the bridge's live
  // rolesTax/skillsTax (from activeFilterMap) with the user's saved-pref
  // adaptive identity. Same reasoning as the filter-prefetch cache —
  // adaptive re-resolution should not fracture the cache into duplicate
  // entries. See `AssistantTaxonomy` docstring.
  const prefsTaxonomyByEntity = useGlobalAssistantTaxonomy();
  const { applyAiSearchFilters, applyScreenerAiSearchFilters } =
    useApplyAiSearchFilters();
  const activeRolesTax = useMemo(
    () => getRoleTaxonomyIdForAiSearch(activeFilterMap),
    [activeFilterMap]
  );
  const activeSkillsTax = useMemo(
    () => getSkillTaxonomyIdFromActiveFilterMap(activeFilterMap),
    [activeFilterMap]
  );

  const getIntentTitleFromUnknown = useCallback(
    (content: unknown): string | null => {
      if (typeof content !== 'object' || content === null) {
        return null;
      }
      const candidate = (content as { title?: unknown }).title;
      return typeof candidate === 'string' ? candidate : null;
    },
    []
  );

  // Latest summary read by async narration callbacks (which resolve after the
  // command handler's closure was created and need the freshest value).
  const conversationSummaryRef = useRef(ctx?.conversationSummary);
  conversationSummaryRef.current = ctx?.conversationSummary;
  // Collector for the in-flight page-create narration. The drawer is the
  // GA host, but the generated widgets and their settled data live in THIS
  // bridge's CustomDash page tree, so collection is armed here. Disposed when
  // a new page-create starts, and on narration completion — nowhere else, so
  // a long-lived bridge never leaks a stale collector.
  const narrationCollectorRef = useRef<NarrationCollector | null>(null);

  const setLastPageCollectionOutcome = useGlobalAiCommandStore(
    (state) => state.setLastPageCollectionOutcome
  );
  const setLastPagePlan = useGlobalAiCommandStore(
    (state) => state.setLastPagePlan
  );
  const publishPageGenProgress = useGlobalAiCommandStore(
    (state) => state.publishPageGenProgress
  );

  const armPageNarration = useCallback(
    (params: {
      prompt: string;
      generatedItemIds: string[];
      conversationSummaryText?: string;
      /**
       * Page-level applied-filter labels summarized from the page build's
       * AI-search `filters` event (same source + summarizer as the inline
       * chart). Stamped onto every collected widget digest so grounded
       * narration knows the widgets are filtered and cannot claim the value
       * is unfiltered or re-offer an already-applied filter (FE-2117).
       */
      filtersApplied?: string[];
    }) => {
      if (params.generatedItemIds.length === 0) {
        return;
      }
      const filtersApplied = params.filtersApplied;
      narrationCollectorRef.current?.dispose();
      const collector = new NarrationCollector({
        expectedItemIds: params.generatedItemIds,
        deadlineTiers: [
          // Majority settled → narrate at 5s instead of waiting on a
          // straggler that usually never settles before the hard timeout. The
          // page is already visible during this wait, so the only cost is a
          // slightly-less-complete digest in exchange for less narration
          // latency on the common (warm) case.
          {
            atMs: 5_000,
            minReported: Math.max(
              1,
              Math.ceil(params.generatedItemIds.length / 2)
            ),
          },
          // Cold-page middle tier: SOMETHING settled → narrate at 12s from
          // what we have instead of riding the 30s backstop. The
          // partial-aware narration contract (client tools) keeps the model
          // honest about still-loading charts.
          { atMs: 12_000, minReported: 1 },
        ],
        // Hard backstop matched to the single-chart settle budget
        // (CHART_SETTLE_TIMEOUT_MS = 30s). A shorter backstop routinely
        // completed with ZERO digests on cold pages — the model then narrated
        // the page blind (inventing absent "compensation"/"sentiment"
        // sections). The collector still completes as soon as every widget
        // reports, so warm/cache-hit pages are unaffected.
        timeoutMs: 30_000,
        onComplete: (digests) => {
          narrationCollectorRef.current = null;
          setActiveNarrationCollector(null);
          // Publish the collected widget digests so the tool loop can await
          // them before narrating. The loop's next text segment IS the
          // narration now — the bridge deliberately does NOT self-narrate or
          // write any transcript message here (that produced a second,
          // duplicate narration).
          setLastPageCollectionOutcome({
            digests:
              filtersApplied && filtersApplied.length > 0
                ? digests.map((digest) =>
                    withChartScope(digest, { filtersApplied })
                  )
                : digests,
            prompt: params.prompt,
            expectedCount: params.generatedItemIds.length,
          });
        },
      });
      narrationCollectorRef.current = collector;
      setActiveNarrationCollector(collector);
    },
    [setLastPageCollectionOutcome]
  );

  // Keep the current page name in a ref so the title-overwrite guard can
  // read it synchronously during SSE event handling without retriggering
  // the command-queue effect on every keystroke in the page header.
  const pageNameRef = useRef(pageConfig.name);
  useEffect(() => {
    pageNameRef.current = pageConfig.name;
  }, [pageConfig.name]);

  const dispatchGeneratedPageTitle = useCallback(
    (args: { title: string | null; mode: 'create' | 'update' }) => {
      if (args.mode !== 'create') {
        return;
      }
      const title = args.title?.trim();
      if (!title) {
        return;
      }
      // Mirror the AIAssistantDrawer guard: only adopt the AI-generated
      // title while the page is still using its auto-generated default
      // ("Untitled" / "Untitled 2" / ...). This keeps user-renamed pages
      // from being silently overwritten — including via the cache-hit
      // path, which applies a cached title after an async filter step.
      if (!isDefaultUntitledName(pageNameRef.current)) {
        return;
      }
      pageDispatch({ type: 'UPDATE_PAGE_TITLE', payload: title });
    },
    [pageDispatch]
  );

  /**
   * Screener v2 public pages (company/geo/role Discovery) have no
   * `tabContent.primaryFilter` — the dimension is implicit from the template.
   * Without this branch `entityTypeNow` is null, the command-queue effect
   * bails, and global-assistant route filters (including screener endpoint
   * responses) never apply.
   */
  const entityTypeNow = (() => {
    if (pageConfig.type === PageType.REPORT) {
      return null;
    }
    if (pageConfig.revelioPageId === RevelioPageId.TALENT_DISCOVERY) {
      return 'talentDiscovery' as const;
    }
    const fromPrimary = getEntityTypeFromPrimaryFilter(
      tabContent.primaryFilter
    );
    if (fromPrimary) {
      return fromPrimary;
    }
    if (isScreenerV2Page(pageConfig.revelioPageId)) {
      const pf = getPrimaryFilterIdForScreenerV2Page(pageConfig.revelioPageId);
      if (pf) {
        const e = filterIdToEntityType(pf);
        if (e === 'company' || e === 'geography' || e === 'role') {
          return e;
        }
      }
    }
    return null;
  })();

  // Subscribe to the queue — bridge re-runs its drain effect whenever the
  // queue changes OR any dep that affects command handling changes.
  const queue = useGlobalAiCommandStore((state) => state.queue);
  const claim = useGlobalAiCommandStore((state) => state.claim);
  const complete = useGlobalAiCommandStore((state) => state.complete);
  const pruneExpired = useGlobalAiCommandStore((state) => state.pruneExpired);
  const publishActiveTaxonomy = useGlobalAiCommandStore(
    (state) => state.publishActiveTaxonomy
  );
  const publishLastAppliedFilterContext = useGlobalAiCommandStore(
    (state) => state.publishLastAppliedFilterContext
  );
  const setLastFilterApplyOutcome = useGlobalAiCommandStore(
    (state) => state.setLastFilterApplyOutcome
  );

  // Publish the current page's taxonomy into the store so the drawer (which
  // lives outside CustomDash and has no filter context) can use the ACTIVE
  // page taxonomy when resolving AI filters. Without this the drawer falls
  // back to saved preferences, which miss session-only overrides (e.g. the
  // user picking a taxonomy in Preferences without clicking "Set as default").
  //
  // Gated on BOTH role and skill taxonomy resolution to avoid publishing
  // a pre-resolution value (see the TODO(backend-adaptive) note on the
  // readiness hooks above).
  //
  // TODO(backend-adaptive): re-evaluate the entire publish effect when
  // the backend accepts "adaptive". The drawer's primary reason for
  // reading `activeTaxonomyByEntity` is the refinement pass; once that's
  // gone, this effect's remaining use is niche session-override support.
  // See the refinement-pass docblock in `global-ai-assistant-drawer.tsx`.
  useEffect(() => {
    if (!enabled || !entityTypeNow) return;
    if (!isRoleTaxonomyResolved || !isSkillTaxonomyResolved) return;
    publishActiveTaxonomy(entityTypeNow, {
      rolesTax: activeRolesTax,
      skillsTax: activeSkillsTax,
    });
    return () => {
      publishActiveTaxonomy(entityTypeNow, undefined);
    };
  }, [
    enabled,
    entityTypeNow,
    activeRolesTax,
    activeSkillsTax,
    isRoleTaxonomyResolved,
    isSkillTaxonomyResolved,
    publishActiveTaxonomy,
  ]);

  // Expose the current CustomDash UI state (edit mode + whether the page
  // already has items) to the drawer so it can include those hints in
  // backend requests (the "should we offer Update vs Create?" routing on
  // the backend keys off this). `useLayoutEffect` so it runs before the
  // drawer's next render; it's a pure ref write, no state change.
  useLayoutEffect(() => {
    if (!ctx) return;
    ctx.customDashAssistContextRef.current = {
      isInCustomDashEditMode: editMode,
      hasCurrentPageItems: (tabContent.items?.length ?? 0) > 0,
      hasUnsavedChanges,
      discardChanges,
    };
    return () => {
      if (!ctx) return;
      ctx.customDashAssistContextRef.current = {
        isInCustomDashEditMode: false,
        hasCurrentPageItems: false,
        hasUnsavedChanges: false,
        discardChanges: undefined,
      };
    };
  }, [ctx, discardChanges, editMode, hasUnsavedChanges, tabContent.items]);

  useEffect(() => {
    if (!enabled || !ctx) {
      return;
    }

    pruneExpired();

    // Find the first queued command this bridge can handle. Only 'queued'
    // commands are eligible — 'in_flight' ones are already being handled,
    // typically by a previous render of this bridge.
    const match = queue.find(
      (q) =>
        q.status === 'queued' &&
        shouldGlobalAiBridgeHandleCommand(q.command, {
          editMode,
          entityTypeNow,
          pathname,
        })
    );

    if (!match) return;
    if (!claim(match.command.requestId, pathname)) return;

    void runCommand(match.command);

    async function runCommand(command: GlobalAiCommand) {
      // Track whether any branch set a user-visible error message. The
      // outer finally only clears the status on success — so a message
      // from a catch block survives until the user takes the next action.
      let errorStatus: string | null = null;
      try {
        if (command.kind === 'applyRouteFilters') {
          if (!ctx) return;
          ctx.setGenerationStatus('Applying filters…');
          // Response was resolved by the drawer in parallel with navigation,
          // so no network call here — just apply.
          try {
            // Choose the applier by the resolved artifact's SHAPE, not by the
            // path-derived `command.filterKind`. The drawer pre-resolves filters
            // against a route-derived endpoint, so a mismatch (e.g. a screener
            // `CompanyDiscoveryAiOutput` carried on a command whose path-derived
            // `filterKind` is `tdV2`) would otherwise hand the response to the
            // wrong applier and corrupt the apply. The SHAPE wins. Only if the
            // shape is unrecognizable do we fall back to `filterKind` so nothing
            // regresses.
            const response = command.response;
            // The SHAPE wins; only fall back to the path-derived `filterKind`
            // when neither guard matches an otherwise unrecognizable artifact.
            let applyAsScreener: boolean;
            if (isScreenerAiOutput(response)) {
              applyAsScreener = true;
            } else if (isTalentDiscoveryFilterResponse(response)) {
              applyAsScreener = false;
            } else {
              applyAsScreener = command.filterKind === 'screener';
            }
            if (applyAsScreener) {
              // Screener-shaped (`filters.entity_info_selectors`). Casting from
              // the resolved union is safe: the shape guard above (or the
              // `filterKind` fallback) selected this branch.
              applyScreenerAiSearchFilters(
                response as Extract<
                  typeof response,
                  { filters: { entity_info_selectors: unknown } }
                >,
                {
                  mergeWithExistingFilters: command.merge === true,
                }
              );
            } else {
              const tdResponse = response as Exclude<
                typeof response,
                { filters: { entity_info_selectors: unknown } }
              >;
              // Discovery surfaces (Individual/Talent Discovery) refine an
              // existing people search, so MERGE the new constraint with the
              // current filters — matching the legacy additive behavior. Only
              // a standard product page (e.g. Company Pay) being scoped fresh
              // should replace its primary (company/geography/role) selection.
              // This is a legitimate PATH concern, so it stays path-based.
              const primarySelectionMergeMode = isTalentDiscoveryPublicPath(
                command.targetPath
              )
                ? 'merge'
                : 'replace';
              await applyAiSearchFilters(tdResponse, {
                primarySelectionMergeMode,
                skipTalentDiscoveryAdaptFallback: true,
                mergeWithExistingFilters: command.merge === true,
                // Resolved-artifact-link applies only (see the command
                // docblock): the page adopts the taxonomy the artifact's
                // role ids were resolved under, so it never queries
                // taxonomy-relative ids under the preference taxonomy.
                ...(command.pairTaxonomy === true
                  ? { pairRoleTaxonomyWithResponse: true }
                  : {}),
              });
              if (command.prompt?.trim()) {
                setLastFilterApplyOutcome({
                  digest: buildFilterResolutionDigest({
                    response: tdResponse,
                    targetPage: pageNameRef.current || pathname,
                  }),
                  prompt: command.prompt,
                });
              }
            }
            if (command.prompt?.trim()) {
              publishLastAppliedFilterContext(command.targetPath, {
                targetPath: command.targetPath,
                surfaceKind: 'dashboard',
                entityType: command.entityType,
                prompt: command.prompt,
              });
            }
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_APPLIED,
              { entity_type: command.entityType }
            );
          } catch (e) {
            const message = e instanceof Error ? e.message : 'Apply failed';
            errorStatus = `Couldn't apply filters: ${message}`;
            // The chat turn already ended optimistically ("applying filters
            // now…"), so a consumed-but-failed apply must surface in-chat
            // too — the drawer watches this (DP4).
            useGlobalAiCommandStore.getState().reportFilterApplyFailure({
              requestId: command.requestId,
              path: command.targetPath,
            });
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_FAILED,
              {
                entity_type: command.entityType,
                global_assistant_route_filter_error: message,
              }
            );
          }
        } else if (command.kind === 'applySingleChart') {
          if (!ctx) return;
          ctx.setGenerationStatus('Applying chart…');
          try {
            const customDashEntityType = toCustomDashAssistantEntityType(
              command.entityType
            );
            if (!customDashEntityType) {
              throw new Error(
                'Custom Dash is not available for Individual Discovery.'
              );
            }
            const primaryFilterId =
              getPrimaryFilterForAssistantEntity(customDashEntityType);
            applyLlmTabContentToTabDispatch(
              dispatch,
              command.config,
              primaryFilterId
            );
            if (command.filters) {
              ctx.setGenerationStatus('Applying filters…');
              await applyAiSearchFilters(command.filters, {
                primarySelectionMergeMode: 'replace',
                skipTalentDiscoveryAdaptFallback: true,
              });
              if (command.prompt?.trim()) {
                publishLastAppliedFilterContext(command.targetPath, {
                  targetPath: command.targetPath,
                  surfaceKind: 'dashboard',
                  entityType: command.entityType,
                  prompt: command.prompt,
                });
                // Deliberately no filter-apply narration here: the model narrates
                // chart turns inline via streamed text on the loop path.
              }
            }
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_CHART_APPLIED,
              { entity_type: command.entityType }
            );
          } catch (e) {
            const message = e instanceof Error ? e.message : 'Apply failed';
            errorStatus = `Couldn't apply chart: ${message}`;
          }
        } else if (command.kind === 'startCustomPageStream') {
          const commandMode = command.mode ?? 'create';
          if (!ctx) return;
          ctx.streamAbortRef.current?.abort();
          const ac = new AbortController();
          ctx.streamAbortRef.current = ac;
          const customDashEntityType = toCustomDashAssistantEntityType(
            command.entityType
          );
          if (!customDashEntityType) {
            ctx.setGenerationStatus(
              'Custom Dash is not available for Individual Discovery.'
            );
            ctx.streamAbortRef.current = null;
            return;
          }
          const rolesTax = getRoleTaxonomyIdForAiSearch(activeFilterMap);
          const skillsTax =
            getSkillTaxonomyIdFromActiveFilterMap(activeFilterMap);

          // Update mode: patch the current page's layout in place using
          // the bounded conversation context (recent turns + summary).
          // Deliberately skips the cache — the current tab content IS
          // the state being updated, so there's no meaningful
          // prompt+taxonomy cache key.
          if (command.mode === 'update') {
            try {
              ctx.setGenerationStatus('Updating page…');
              let sawQuestions = false;
              await runDashboardPageStreamUpdate({
                newPrompt: command.prompt,
                entityType: customDashEntityType,
                currentConfig: convertTabContentToLLM(tabContent),
                currentFilterContext:
                  buildAiSearchCurrentFilterContext(activeFilterMap),
                previousPrompts: getPreviousPromptsForUpdateCommand(command),
                model: command.model,
                answers: command.answers,
                rolesTax,
                skillsTax,
                conversation: command.conversation,
                dispatch,
                applyAiSearchFilters,
                onEvent: (ev) => {
                  if (ev.type === 'questions') {
                    sawQuestions = true;
                    ctx.appendTranscript({
                      id: crypto.randomUUID(),
                      role: 'assistant',
                      kind: 'clarification',
                      text: formatClarificationQuestionsAsText({
                        triggerReason: ev.content.triggerReason,
                        questions: ev.content.questions,
                        emptyFallback:
                          'I need a bit more detail to update that page.',
                      }),
                    });
                    ctx.setGenerationStatus(null);
                  } else if (ev.type === 'error') {
                    ctx.setGenerationStatus(ev.content);
                  }
                  // Publish structured progress (thinking/intent/status) so the
                  // turn runner's update subscription streams the update's live
                  // reasoning to the activity lane — same single source as the
                  // create path (so an update no longer reads as a dead "Updating
                  // page…" wait).
                  const progress = dashboardStreamEventToProgress(ev);
                  if (progress) {
                    publishPageGenProgress(progress);
                  }
                },
                signal: ac.signal,
              });
              if (sawQuestions) {
                return;
              }
              mixpanel.track(
                BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_PAGE_STREAM_DONE,
                {
                  entity_type: command.entityType,
                  global_assistant_page_stream_cache_hit: false,
                }
              );
            } catch (e) {
              errorStatus = e instanceof Error ? e.message : 'Update failed';
            } finally {
              ctx.streamAbortRef.current = null;
            }
            return;
          }

          // Cache-key identity from saved prefs (stable across adaptive
          // re-resolution); API payload from the bridge's live filter
          // map (reflects current page state).
          const prefsTax = prefsTaxonomyByEntity(customDashEntityType);
          const cacheTaxonomy = {
            rolesTax,
            skillsTax,
            rolesKey: prefsTax.rolesKey,
            skillsKey: prefsTax.skillsKey,
          };
          const shouldUsePageStreamCache =
            !command.answers || command.answers.length === 0;

          // Fast path: if we've already generated a layout for this
          // prompt + entity + taxonomy within the cache TTL, skip the
          // SSE stream and apply the cached config + filters directly.
          const cached = shouldUsePageStreamCache
            ? readCachedPageStream(
                command.prompt,
                customDashEntityType,
                cacheTaxonomy
              )
            : null;
          if (cached) {
            try {
              ctx.setGenerationStatus('Reusing cached layout…');
              const primaryFilterId =
                getPrimaryFilterForAssistantEntity(customDashEntityType);
              // Capture the items actually dispatched to the tab. Their ids
              // are what the widgets mount with, so the narration collector
              // MUST be armed with these exact ids — re-converting the LLM
              // content would mint fresh uuids that never match (FE-2117).
              const { items: dispatchedItems } =
                applyLlmTabContentToTabDispatch(
                  dispatch,
                  cached.config,
                  primaryFilterId
                );
              dispatchGeneratedPageTitle({
                title: cached.title ?? null,
                mode: commandMode,
              });
              if (cached.filters) {
                await applyAiSearchFilters(cached.filters, {
                  primarySelectionMergeMode: 'replace',
                  skipTalentDiscoveryAdaptFallback: true,
                });
              }
              armPageNarration({
                prompt: command.prompt,
                // Text items have no data-provider and never emit a digest — exclude
                // them so the collector doesn't wait out the full timeout for them.
                generatedItemIds: dispatchedItems
                  .filter((item) => item.plot?.chartType !== 'text')
                  .map((item) => item.id),
                filtersApplied: summarizeAppliedChartFilters(cached.filters),
                ...(conversationSummaryRef.current?.text
                  ? {
                      conversationSummaryText:
                        conversationSummaryRef.current.text,
                    }
                  : {}),
              });
              // Cache hit still settles widget data over several seconds, so
              // publish the plan titles here too (mirrors the cache-miss path)
              // for the tool loop's grounded "plan" narration line.
              setLastPagePlan({
                prompt: command.prompt,
                entityType: customDashEntityType,
                titles: dispatchedItems
                  .filter((item) => item.plot?.chartType !== 'text')
                  .map((item) => item.title)
                  .filter((title): title is string => Boolean(title)),
              });
              mixpanel.track(
                BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_PAGE_STREAM_DONE,
                {
                  entity_type: command.entityType,
                  global_assistant_page_stream_cache_hit: true,
                }
              );
            } catch (e) {
              errorStatus = e instanceof Error ? e.message : 'Apply failed';
            } finally {
              ctx.streamAbortRef.current = null;
            }
            return;
          }

          // Miss — run the stream, capture config + filters for the
          // cache along the way. Only write cache on successful
          // completion (no throw). Multiple config events may arrive
          // during the stream; the last one wins (matches how the
          // stream runner applies them — each call replaces the
          // previous dispatch).
          try {
            ctx.setGenerationStatus('Generating page…');
            let capturedConfig: CachedPageStreamResult['config'] | undefined;
            let capturedFilters: CachedPageStreamResult['filters'] | undefined;
            let capturedTitle: CachedPageStreamResult['title'] | undefined;
            let sawQuestions = false;
            // First-title-wins guard. `pageNameRef` is synced via effect,
            // so it cannot block subsequent intents that arrive within the
            // same SSE chunk (processed synchronously). Mirror the hooks'
            // synchronous `conversationTitleRef` pattern with a local flag.
            let titleDispatched = false;
            const { dispatchedItems } = await runDashboardPageStreamCreate({
              prompt: command.prompt,
              entityType: customDashEntityType,
              model: command.model,
              answers: command.answers,
              rolesTax,
              skillsTax,
              conversation: command.conversation,
              dispatch,
              applyAiSearchFilters,
              onEvent: (ev) => {
                if (ev.type === 'intent') {
                  const intentTitle = getIntentTitleFromUnknown(ev.content);
                  // First-title-wins for the cache: keep the earliest
                  // non-null title so a cached replay dispatches the same
                  // page title the user originally saw, and so a later
                  // titleless intent can't clear it (which would make the
                  // cache-hit path bail on subsequent runs).
                  capturedTitle = capturedTitle ?? intentTitle ?? undefined;
                  if (!titleDispatched && intentTitle) {
                    titleDispatched = true;
                    dispatchGeneratedPageTitle({
                      title: intentTitle,
                      mode: commandMode,
                    });
                  }
                }
                if (ev.type === 'config') {
                  capturedConfig = ev.content;
                } else if (ev.type === 'filters') {
                  capturedFilters = ev.content;
                } else if (ev.type === 'questions') {
                  sawQuestions = true;
                  ctx.appendTranscript({
                    id: crypto.randomUUID(),
                    role: 'assistant',
                    kind: 'clarification',
                    text: formatClarificationQuestionsAsText({
                      triggerReason: ev.content.triggerReason,
                      questions: ev.content.questions,
                      emptyFallback:
                        'I need a bit more detail to build that page.',
                    }),
                  });
                  ctx.setGenerationStatus(null);
                } else if (ev.type === 'error') {
                  ctx.setGenerationStatus(ev.content);
                }
                // Publish structured progress (thinking/intent/status) for the
                // activity lane. The turn runner subscribes and drives the
                // activity-lane string through the progress channel — single
                // source, so we no longer set it directly here for `status`.
                const progress = dashboardStreamEventToProgress(ev);
                if (progress) {
                  publishPageGenProgress(progress);
                }
              },
              signal: ac.signal,
            });
            if (sawQuestions) {
              return;
            }
            if (capturedConfig) {
              // Reuse the items the stream runner actually dispatched to the
              // tab — their ids are what the widgets mounted with. Re-converting
              // `capturedConfig` here would mint fresh, non-matching uuids and
              // the collector would time out with an empty digest (FE-2117).
              armPageNarration({
                prompt: command.prompt,
                // Text items have no data-provider and never emit a digest — exclude
                // them so the collector doesn't wait out the full timeout for them.
                generatedItemIds: dispatchedItems
                  .filter((item) => item.plot?.chartType !== 'text')
                  .map((item) => item.id),
                filtersApplied: summarizeAppliedChartFilters(capturedFilters),
                ...(conversationSummaryRef.current?.text
                  ? {
                      conversationSummaryText:
                        conversationSummaryRef.current.text,
                    }
                  : {}),
              });
              // Publish the chart titles for the in-flight build so the tool
              // loop can stream a grounded "plan" line while the widgets'
              // data settle. Exclude text widgets (no chart, no useful title).
              setLastPagePlan({
                prompt: command.prompt,
                entityType: customDashEntityType,
                titles: dispatchedItems
                  .filter((item) => item.plot?.chartType !== 'text')
                  .map((item) => item.title)
                  .filter((title): title is string => Boolean(title)),
              });
            }
            if (capturedConfig && shouldUsePageStreamCache) {
              writeCachedPageStream(
                command.prompt,
                customDashEntityType,
                cacheTaxonomy,
                {
                  config: capturedConfig,
                  ...(capturedFilters ? { filters: capturedFilters } : {}),
                  ...(capturedTitle ? { title: capturedTitle } : {}),
                }
              );
            }
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_PAGE_STREAM_DONE,
              {
                entity_type: command.entityType,
                global_assistant_page_stream_cache_hit: false,
              }
            );
          } catch (e) {
            errorStatus = e instanceof Error ? e.message : 'Generation failed';
          } finally {
            ctx.streamAbortRef.current = null;
          }
        }
      } finally {
        complete(command.requestId);
        // Only clear the status on success. If a branch set `errorStatus`,
        // surface it to the user — they'll see why the flow failed instead
        // of being returned to a silent empty page.
        if (errorStatus === null) {
          ctx?.setGenerationStatus(null);
        } else {
          ctx?.setGenerationStatus(errorStatus);
        }
      }
    }
  }, [
    enabled,
    editMode,
    ctx,
    entityTypeNow,
    pathname,
    tabContent,
    dispatch,
    activeFilterMap,
    applyAiSearchFilters,
    applyScreenerAiSearchFilters,
    queue,
    claim,
    complete,
    pruneExpired,
    publishLastAppliedFilterContext,
    setLastFilterApplyOutcome,
    prefsTaxonomyByEntity,
    dispatchGeneratedPageTitle,
    getIntentTitleFromUnknown,
    armPageNarration,
    setLastPagePlan,
    publishPageGenProgress,
  ]);

  return null;
}
